import type { ProductTourPageId } from "./product-tour";
import type { ControlTourStep } from "./control-tour.model";

const scopeSteps = (page: "patterns" | "trends"): ControlTourStep[] => [
  {
    target: "button",
    label: "Change filters",
    title: "Change filters",
    body: "Show the format and clock steps to change which games contribute to this page.",
  },
  {
    target: '[aria-label="Performance selection"] nav > div:nth-child(1) button',
    title: "Format",
    body: "Return to the format choice to study Rapid, Blitz, Bullet, or Daily. Evidence stays separated by format.",
  },
  {
    target: `[data-tour="${page}-format-picker"] button`,
    title: "Choose a format",
    body: "Choose the format you want to study. Counts show how much analyzed evidence is available for each choice.",
  },
  {
    target: '[aria-label="Performance selection"] nav > div:nth-child(2) button',
    title: "Clock",
    body: "Return to the clock choice to compare games played with the same time limit. Choose a format first.",
  },
  {
    target: `[data-tour="${page}-clock-picker"] button`,
    title: "Choose a clock",
    body: "Choose an exact time limit, including its increment, or All clocks within the selected format.",
  },
  {
    target: '[aria-label="Performance selection"] nav > div:nth-child(3) button',
    title: "Selected evidence",
    body: "This step shows the results for your current scope. Change Format or Clock to rebuild that scope.",
  },
];

export const EVIDENCE_CONTROL_TOURS: Partial<Record<ProductTourPageId, ControlTourStep[]>> = {
  home: [
    {
      target: '[data-tour="home-review-today"]',
      title: "Review today",
      body: "Choose one of today's analyzed games to review its saved positions. Today follows your device's time zone.",
    },
    {
      target: "button",
      label: "MY MISTAKES",
      title: "My mistakes",
      body: "Inspect saved mistakes from up to three recent games in the selected format. Choose a move to open its exact position. Opponent mistakes are excluded.",
    },
    {
      target: '[data-tour="home-analyze-waiting"]',
      title: "Analyze waiting",
      body: "Preview eligible games that still need analysis. Choose the analysis depth and confirm before anything starts; existing analyses stay untouched.",
    },
    {
      target: "a",
      label: "LAST GAME",
      title: "Last game",
      body: "Open your most recent imported match on Chess.com in a new tab. Your dashboard stays open.",
    },
    {
      target: "button",
      label: "BEFORE YOU PLAY",
      title: "Before you play",
      body: "Read one practical reminder from your selected format's current focus. Open its supporting pattern to inspect the evidence. No new Nikola request is needed.",
    },
    {
      target: '[aria-labelledby="home-play-title"] a[target="_blank"]',
      title: "Play",
      body: "Open Chess.com with one of your most-played clocks. Sign in and press Play there if needed. Your Chessla page stays open.",
    },
    {
      target: '[aria-label="Import and analyze today\u0027s games"]',
      title: "Import today",
      body: "Bring in completed games from today in your device\u0027s time zone, then run Regular analysis on those still waiting. Existing analyses are kept; progress appears in the sidebar.",
    },
    {
      target: "label",
      label: "Game format",
      title: "Game format",
      body: "Choose which format supplies your dashboard. Results, patterns, and your next focus use that format.",
    },
    {
      target: "a",
      label: "Continue review",
      title: "Continue review",
      body: "Resume the saved game review where you left off.",
    },
    {
      target: "a",
      label: "Review game",
      title: "Review game",
      body: "Open this analyzed game to inspect its recorded moves and verified alternatives.",
    },
    {
      target: "a",
      label: "Analyze a game",
      title: "Analyze a game",
      body: "Your imported games need analysis before they can supply verified review evidence. Open Analyze to choose a game.",
    },
    {
      target: "a",
      label: "Import games",
      title: "Import games",
      body: "Open Analyze to bring finished games into your library before analyzing them.",
    },
    {
      target: "a",
      label: "Open Archive",
      title: "Open Archive",
      body: "Open your game library when dashboard evidence is unavailable.",
    },
    {
      target: "#home-focus-title",
      title: "Your next focus",
      body: "Use this evidence-based habit in your next game. Its supporting pattern comes from analyzed games in the selected format.",
    },
    {
      target: "#home-form-title",
      title: "Rating at game time",
      body: "Follow the ratings recorded with your games. This shows your selected format's history, rather than a live rating lookup.",
    },
    {
      target: "#home-results-title",
      title: "Recent results",
      body: "Compare wins, draws, and losses in the recent imported sample. Games can contribute results before they have analysis.",
    },
    {
      target: '[aria-labelledby="home-results-title"] a',
      title: "Open a recent game",
      body: "Choose a result to inspect that game and connect the summary to an actual opponent and result.",
    },
    {
      target: "#home-weaknesses-title",
      title: "Pattern radar",
      body: "See recurring verified moments in analyzed games. Affected-game counts describe the sample; they are not statistical confidence.",
    },
    {
      target: '[aria-labelledby="home-weaknesses-title"] a',
      title: "Inspect a pattern",
      body: "Open Patterns to inspect the game evidence behind a recurring issue.",
    },
    {
      target: "#home-progress-title",
      title: "Your direction",
      body: "Compare earlier and recent evidence for the selected format. Read the sample size alongside any change.",
    },
    {
      target: "#home-position-title",
      title: "Saved position",
      body: "Inspect a verified moment from one of your games. The board preserves your playing color and the recorded position.",
    },
    {
      target: '[aria-labelledby="home-position-title"] button[aria-label]',
      title: "Step through the line",
      body: "Use the board controls to inspect the saved continuation one move at a time. This does not change the recorded game.",
    },
  ],
  games: [
    {
      target: "button",
      label: "Change filters",
      title: "Change filters",
      body: "Show the format and clock choices to change which games appear.",
    },
    {
      target: '[aria-label="Game selection steps"] > div:nth-child(1) button',
      title: "Format",
      body: "Return to the format choice when you want to review a different type of game.",
    },
    {
      target: '[data-tour="games-format-picker"] button',
      title: "Choose a format",
      body: "Choose Rapid, Blitz, Bullet, or Daily to narrow your library before picking a clock.",
    },
    {
      target: '[aria-label="Game selection steps"] > div:nth-child(2) button',
      title: "Clock",
      body: "Return to the time-limit choice within this format. Select a format before choosing its clock.",
    },
    {
      target: '[data-tour="games-clock-picker"] button',
      title: "Choose a clock",
      body: "Choose an exact time limit or All clocks. Games with different increments remain distinguishable.",
    },
    {
      target: '[aria-label="Game selection steps"] > div:nth-child(3) button',
      title: "Games in this selection",
      body: "The final step lists games matching your format and clock. Use the earlier steps to change that selection.",
    },
    {
      target: '[aria-label="Search games by opponent or opening"]',
      title: "Search games",
      body: "Type an opponent or opening to narrow the selected games. Clear the field to restore the list.",
    },
    {
      target: '[aria-label="Change game filter"]',
      title: "Imported or analyzed games",
      body: "Switch between Showing all games and Showing analyzed games. Imported games appear before analysis; verified review metrics require analysis.",
    },
    {
      target: '[role="button"]',
      label: "Result",
      title: "Sort by result",
      body: "Group the list by result. Click again to reverse the sorting direction.",
    },
    {
      target: '[role="button"]',
      label: "Opponent",
      title: "Sort by opponent",
      body: "Order opponents by name to find repeated matches.",
    },
    {
      target: '[role="button"]',
      label: "Rating",
      title: "Sort by rating",
      body: "Order games by the displayed rating to compare the opposition you faced.",
    },
    {
      target: '[role="button"]',
      label: "Date",
      title: "Sort by date",
      body: "Switch between newer and older games without changing the selected format or clock.",
    },
    {
      target: '[role="button"]',
      label: "Opening",
      title: "Sort by opening",
      body: "Bring games with the same opening together for comparison.",
    },
    {
      target: '[role="button"]',
      label: "Accuracy",
      title: "Sort by accuracy",
      body: "Compare available analysis accuracy. Imported games need analysis before this metric is available.",
    },
    {
      target: '[aria-label^="Review game against "]',
      title: "Review an analyzed game",
      body: "Open the recorded game with its verified moves, turning points, and alternatives.",
    },
    {
      target: '[aria-label^="Analyze game against "]',
      title: "Analyze an imported game",
      body: "Choose analysis for this game to produce verified review evidence. Importing alone does not calculate it.",
    },
  ],
  analyze: [
    {
      target: '[data-tour="analysis-preset-quick"]',
      title: "Quick analysis",
      body: "Use a faster pass to find major evaluation swings and short tactical lines. Read the warning before choosing it for deeper study.",
    },
    {
      target: '[data-tour="analysis-preset-balanced"]',
      title: "Regular analysis",
      body: "Choose a balanced analysis depth for everyday game reviews and verified tactical lines.",
    },
    {
      target: '[data-tour="analysis-preset-deep"]',
      title: "Deep analysis",
      body: "Choose deeper analysis for difficult positions and longer candidate lines. It requires more processing time.",
    },
    {
      target: '[aria-label="Target game format"] button',
      title: "Target game format",
      body: "Choose which format to import and analyze. This selection determines which games enter the queue.",
    },
    {
      target: '[aria-label="Target game clock"] button',
      title: "Target game clock",
      body: "Narrow the target to a specific time limit, including increment, before importing or analyzing.",
    },
    {
      target: '[data-tour="analysis-import"]',
      title: "Import games",
      body: "Import finished Chess.com games matching the target format and clock. Imported games wait for analysis before they have verified review evidence.",
    },
    {
      target: '[data-tour="analysis-stop-import"]',
      title: "Stop importing",
      body: "Stop the active import when you no longer want to fetch more games.",
    },
    {
      target: '[data-tour="analysis-start"]',
      title: "Start analysis",
      body: "Run the selected depth on waiting games. You need imported games, available analysis access, and a supported browser; the control explains unmet prerequisites.",
    },
    {
      target: '[data-tour="analysis-stop"]',
      title: "Stop analysis",
      body: "Stop the active analysis queue from this control. Completed saved reviews remain available in your history.",
    },
    {
      target: '[data-tour="analysis-history-summary"]',
      title: "Expand analysis history",
      body: "Open a saved entry to see its analysis metrics and review actions. History appears after games finish analysis.",
    },
    {
      target: '[data-tour="analysis-history"] a:not([target])',
      title: "Open a saved review",
      body: "Open an analyzed game from history to inspect its verified results.",
      reveal: '[data-tour="analysis-history-summary"][data-tour-expanded="false"]',
    },
    {
      target: '[data-tour="analysis-history"] button',
      label: "Re-analyze",
      title: "Re-analyze a game",
      body: "Choose another analysis pass for a saved game. This control is unavailable while analysis is running.",
      reveal: '[data-tour="analysis-history-summary"][data-tour-expanded="false"]',
    },
    {
      target: '[data-tour="analysis-history"] button',
      label: "Verify tactics",
      title: "Verify tactics",
      body: "Re-analyze this saved game when its tactical evidence needs verification.",
      reveal: '[data-tour="analysis-history-summary"][data-tour-expanded="false"]',
    },
  ],
  patterns: [
    ...scopeSteps("patterns"),
    {
      target: '[aria-label="Development overview"] button[aria-label]:not([aria-label^="About "])',
      title: "Choose a skill",
      body: "Select a development area to narrow the pattern workspace. Its maturity label reflects the available evidence.",
    },
    {
      target: '[aria-label="Development overview"] button[aria-label^="About "]',
      title: "Understand a skill",
      body: "Open the information button to see what Nikola measures and what improvement looks like for this skill.",
    },
    {
      target: '[data-testid="pattern-picker"] button',
      title: "Choose a pattern",
      body: "Select a recurring issue to inspect its own verified moments and sample counts.",
    },
    {
      target: '[data-testid="active-pattern-label"]',
      title: "Selected pattern evidence",
      body: "This label identifies the pattern currently shown on the board. Evidence is tied to actual games in your selected format.",
    },
    {
      target: '[aria-label="Previous evidence"]',
      title: "Previous evidence",
      body: "Move to the previous saved example of this pattern. The control is disabled at the first example.",
    },
    {
      target: '[aria-label="Next evidence"]',
      title: "Next evidence",
      body: "Compare another recorded example to see how the same issue appears across games. The control is disabled at the last example.",
    },
    {
      target: '[data-testid="pattern-evidence"] button',
      label: "WHAT HAPPENED",
      title: "What happened",
      body: "Inspect the recorded move and its consequence in the original game.",
    },
    {
      target: '[data-testid="pattern-evidence"] button',
      label: "BETTER IDEA",
      title: "Better idea",
      body: "Switch to the verified alternative and compare it with what happened in the game.",
    },
    {
      target: '[data-testid="pattern-evidence"] button',
      label: "INSPECT YOUR MOVE",
      title: "Inspect your move",
      body: "Inspect the move you tried in the temporary position. The recorded game remains unchanged.",
    },
    {
      target: '[data-testid="pattern-evidence"] button',
      label: "PLAY VERIFIED CONTINUATION",
      title: "Play verified continuation",
      body: "Follow the saved verified line to inspect the consequence move by move.",
    },
  ],
  trends: [
    ...scopeSteps("trends"),
    {
      target: "h2",
      label: "Rating and results",
      title: "Rating and results",
      body: "Compare observed rating and results within the selected scope. Forecast ranges describe uncertainty rather than guaranteed future results.",
    },
    {
      target: '[aria-label="Share of games against stronger, similar, and lower-rated opponents."]',
      title: "Opponent strength",
      body: "Check whether your opponents were stronger, similarly rated, or lower rated when interpreting your results.",
    },
    {
      target: "h2",
      label: "Decision quality",
      title: "Decision quality",
      body: "Compare analyzed decision quality over time. Missing evidence messages explain when the sample cannot support a comparison.",
    },
    {
      target: "p",
      label: "CONVERSION",
      title: "Conversion",
      body: "Read how often qualifying advantages became successful results. The denominator counts qualifying games, not your whole library.",
    },
    {
      target: "p",
      label: "SAVES",
      title: "Saves",
      body: "Read successful saves among qualifying games. Compare the sample and interval alongside the percentage.",
    },
    {
      target: '[aria-label="Error rate by game phase."]',
      title: "Errors by phase",
      body: "Compare where errors occur in the opening, middlegame, and endgame. Each phase needs its own eligible evidence.",
    },
    {
      target: "h2",
      label: "Decisions under pressure",
      title: "Decisions under pressure",
      body: "Compare errors made under clock pressure. Daily games do not supply comparable clock decisions.",
    },
    {
      target: '[aria-label="Weekly game activity; intensity is games played, not strength."]',
      title: "Weekly activity",
      body: "Read how much you played each week. Higher intensity means more games, not stronger play.",
    },
    {
      target: '[aria-label="Games played by UTC weekday."]',
      title: "Activity by weekday",
      body: "Compare game counts across weekdays. Dates in this chart use UTC.",
    },
    {
      target: '[aria-label="Games played by UTC time of day."]',
      title: "Activity by time of day",
      body: "See when you play most often. Time-of-day groups use UTC rather than your local timezone.",
    },
    {
      target: '[aria-label="Opening repertoire sized by games played."]',
      title: "Opening repertoire",
      body: "Compare your most played openings. Area represents game count rather than opening quality.",
    },
  ],
  archive: [
    {
      target: "label",
      label: "Format",
      title: "Filter by format",
      body: "Choose Rapid, Blitz, Bullet, or Daily, or keep All games to manage the complete library.",
    },
    {
      target: "label",
      label: "Clock",
      title: "Filter by clock",
      body: "Choose a format first to enable this filter, then select its exact clock or All clocks.",
    },
    {
      target: "label",
      label: "Games",
      title: "Filter by analysis status",
      body: "Choose Imported + analyzed, Analyzed only, or Not analyzed yet to find the games you want to manage.",
    },
    {
      target: '[aria-label="Select all games on this page"]',
      title: "Select this page",
      body: "Select or clear the games on the current page. Selection does not include every page of the archive.",
    },
    {
      target: '[aria-label^="Select game against "]',
      title: "Select one game",
      body: "Add this game to the selection for batch analysis or deletion.",
    },
    {
      target: "button",
      label: "Analyze selected",
      title: "Analyze selected",
      body: "Batch analysis accepts games not yet analyzed and within your current allowance. Select only eligible games; active work also disables this control.",
    },
    {
      target: "button",
      label: "Delete selected",
      title: "Delete selected",
      body: "Request deletion of the selected games and review the confirmation before proceeding. No games are deleted during this tour.",
    },
    {
      target: '[role="button"]',
      label: "Played",
      title: "Sort by date",
      body: "Order archived games by the date played. Click again to reverse the direction.",
    },
    {
      target: '[role="button"]',
      label: "Opponent",
      title: "Sort by opponent",
      body: "Group games by opponent to find repeated matches.",
    },
    {
      target: '[role="button"]',
      label: "Result",
      title: "Sort by result",
      body: "Group wins, draws, and losses within the filtered archive.",
    },
    {
      target: '[role="button"]',
      label: "Format",
      title: "Sort by format",
      body: "Order games by their format while keeping the current archive filters.",
    },
    {
      target: '[role="button"]',
      label: "Status",
      title: "Sort by analysis status",
      body: "Separate imported games from analyzed games. The row badge shows IMPORTED or the saved analysis preset.",
    },
    {
      target: "tbody button",
      label: "Analyze",
      title: "Analyze one game",
      body: "Open analysis choices for this imported game. Available access and an idle analysis queue are required.",
    },
    {
      target: "tbody button",
      label: "Re-analyze",
      title: "Re-analyze one game",
      body: "Run another analysis pass on a game that already has saved results.",
    },
    {
      target: "tbody a:not([target])",
      title: "Open game review",
      body: "Open this game's review from its archive row.",
    },
    {
      target: "tbody a[target]",
      title: "Open original game",
      body: "Open the source game on Chess.com in another tab.",
    },
    {
      target: '[aria-label^="Delete game against "]',
      title: "Delete one game",
      body: "Request deletion of this game. Review the confirmation before committing the deletion.",
    },
    {
      target: "button",
      label: "Previous",
      title: "Previous page",
      body: "Return to the preceding archive page. This is disabled on the first page or while loading.",
    },
    {
      target: "button",
      label: "Next",
      title: "Next page",
      body: "Load the next archive page. This is disabled when no later page exists or while loading.",
    },
    {
      target: "button",
      label: "Stop analysis",
      title: "Stop analysis",
      body: "Stop an active analysis queue without leaving the archive.",
    },
  ],
};
